import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api, parseErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";

const CENTS = (c) => (c == null ? "—" : `$${(Math.abs(c) / 100).toFixed(2)}`);
const XERO_MSG = "Your official tax invoice will be sent separately by email from Xero.";

export default function OrderSuccess() {
  const nav = useNavigate();
  const { orderId } = useParams();
  const { clear, cart, setQty } = useCart();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get(`/orders/${orderId}`);
      setOrder(r.data);
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const onCancel = useCallback(async () => {
    setCancelSubmitting(true); setCancelError("");
    try {
      const r = await api.post(`/orders/${orderId}/cancel`, {
        reason: (cancelReason || "").trim() || null,
      });
      setOrder(r.data);
      setCancelOpen(false);
    } catch (err) {
      setCancelError(parseErrorDetail(err));
    } finally {
      setCancelSubmitting(false);
    }
  }, [orderId, cancelReason]);

  const onEdit = useCallback(() => {
    // Copy the original into the cart, then redirect to /portal/core.
    // We do NOT cancel the original here — the atomic replace happens
    // when the buyer resubmits via /portal/summary.
    if (!order?.lines) return;
    // Reset the cart to avoid mixing with previous work.
    Object.keys(cart || {}).forEach((k) => setQty(k, 0));
    order.lines.forEach((l) => setQty(l.product_id, l.qty));
    // Stash the original order id in sessionStorage so OrderSummary knows to
    // call /edit-replace instead of /submit on button press.
    try {
      sessionStorage.setItem("apwo_edit_replaces_order_id", order.order_id);
      sessionStorage.setItem("apwo_edit_replaces_order_number", order.order_number || "");
    } catch (_) {}
    nav("/portal/core");
  }, [order, nav, cart, setQty]);

  if (loading) {
    return (
      <PortalLayout title="Order Received">
        <div className="apwo-panel" data-testid="order-success-loading">
          <p className="apwo-muted">Loading order…</p>
        </div>
      </PortalLayout>
    );
  }

  if (error || !order) {
    return (
      <PortalLayout title="Order Received">
        <div className="apwo-panel apwo-empty-panel" data-testid="order-success-error">
          <h3>We couldn&apos;t load your order.</h3>
          <p className="apwo-muted">{error || "Please try again in a moment."}</p>
          <div className="apwo-empty-actions">
            <button className="apwo-btn-secondary" onClick={() => nav("/portal/core")} data-testid="order-success-back">
              Back to Core Products
            </button>
          </div>
        </div>
      </PortalLayout>
    );
  }

  const money = order.money || {};
  const isCancelled = !!order.cancelled_at;
  const isReplaced = !!order.replaced_by_order_id;

  return (
    <PortalLayout title="Order Received">
      <div className={`apwo-panel apwo-panel--success ${isCancelled ? "apwo-panel--success--cancelled" : ""}`} data-testid="order-success-panel">
        <div className="apwo-success-head" data-testid="order-success-head">
          <span className={`apwo-success-badge ${isCancelled ? "apwo-success-badge--cancelled" : ""}`}>
            {isCancelled ? "Cancelled" : "Received"}
          </span>
          <h2>Order Request <span data-testid="order-number">{order.order_number}</span></h2>
          <p className="apwo-muted" data-testid="order-timestamp">Submitted {new Date(order.created_at).toLocaleString()}</p>
        </div>

        <div className="apwo-op-message" data-testid="operational-message">
          Order request received. Availability will be confirmed and payment
          instructions (Prepay) will be sent separately. Dispatch occurs after
          cleared funds (Prepay) or on delivery (COD).
          <br/>
          <strong data-testid="xero-handoff-note">{XERO_MSG}</strong>
        </div>

        {isCancelled && (
          <div className="apwo-cancellation-record" data-testid="cancellation-record">
            <p><strong>Cancellation type:</strong> {order.cancellation_type}</p>
            <p><strong>Cancelled at (Sydney):</strong> {order.cancelled_at_sydney || order.cancelled_at}</p>
            <p><strong>By:</strong> {order.cancelled_by}</p>
            {order.cancellation_reason && <p><strong>Reason:</strong> {order.cancellation_reason}</p>}
            {order.reward_restored_cents > 0 && (
              <p><strong>Reward restored:</strong> {CENTS(order.reward_restored_cents)}</p>
            )}
            {order.replaced_by_order_id && (
              <p data-testid="replaced-by-link">
                Replaced by <button className="apwo-linky" onClick={() => nav(`/portal/order/success/${order.replaced_by_order_id}`)}>later order</button>.
              </p>
            )}
          </div>
        )}
        {!isCancelled && order.replaces_order_id && (
          <div className="apwo-cancellation-record" data-testid="replaces-record">
            <p>This order replaces an earlier cancelled order (ID: <code>{order.replaces_order_id}</code>).</p>
          </div>
        )}

        <div className="apwo-success-grid">
          <div className="apwo-panel apwo-panel--summary" data-testid="order-success-lines">
            <h3 className="apwo-summary-section">Items requested</h3>
            <table className="apwo-summary-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th className="apwo-th-right">Qty</th>
                  <th className="apwo-th-right">Unit (ex GST)</th>
                  <th className="apwo-th-right">Line (ex GST)</th>
                </tr>
              </thead>
              <tbody>
                {(order.lines || []).map((l) => (
                  <tr key={l.product_id} data-testid={`success-line-${l.product_id}`}>
                    <td>{l.display_name}</td>
                    <td className="apwo-cell-muted">{l.product_id}</td>
                    <td className="apwo-th-right">{l.qty}</td>
                    <td className="apwo-th-right">{CENTS(l.unit_price_cents)}</td>
                    <td className="apwo-th-right">{CENTS(l.line_total_ex_gst_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {order.customer_note && (
              <div className="apwo-note-block" data-testid="order-success-note">
                <p className="apwo-ship-title">Your note</p>
                <p className="apwo-note-view">{order.customer_note}</p>
              </div>
            )}
          </div>

          <aside className="apwo-panel apwo-panel--totals" data-testid="order-success-totals">
            <h3 className="apwo-panel-heading">Confirmed totals</h3>
            <div className="apwo-totals-row">
              <span>Product subtotal (ex GST)</span>
              <span data-testid="success-tot-product-ex">{CENTS(money.product_subtotal_ex_gst_cents)}</span>
            </div>
            <div className="apwo-totals-row">
              <span>Freight (ex GST)</span>
              <span data-testid="success-tot-freight-ex">{CENTS(money.freight_ex_gst_cents)}</span>
            </div>
            <div className="apwo-totals-row">
              <span>GST (10%)</span>
              <span data-testid="success-tot-gst">{CENTS(money.gst_total_cents)}</span>
            </div>
            <div className="apwo-totals-row apwo-totals-row--total">
              <span>TOTAL PAYABLE (incl. GST)</span>
              <span data-testid="success-tot-payable">{CENTS(money.payable_total_cents)}</span>
            </div>

            <div className="apwo-pay-block" data-testid="success-status">
              <p className="apwo-pay-title">Status</p>
              <p className="apwo-cell-muted">
                Payment term: <strong data-testid="success-payment-term">{order.payment_term || "—"}</strong><br/>
                Order status: <strong data-testid="success-order-status">{order.order_status}</strong><br/>
                Payment status: <strong data-testid="success-payment-status">{order.payment_status}</strong>
                {Number(order?.money?.reward_applied_cents || 0) > 0 && (
                  <><br/>Reward applied to this order: <strong data-testid="success-reward-applied">-{CENTS(order.money.reward_applied_cents)}</strong></>
                )}
                <br/>Remaining payable: <strong data-testid="success-remaining-payable">{CENTS(order?.money?.payable_total_cents)}</strong>
                {order.earned_reward_cents > 0 && (
                  <><br/>Reward earned from this order: <strong data-testid="success-earned-reward">{CENTS(order.earned_reward_cents)}</strong></>
                )}
              </p>
            </div>

            {!isCancelled && !isReplaced && (order.is_cancellable || order.cancellation_block_reason) && (
              <div className="apwo-lifecycle-actions" data-testid="order-lifecycle-actions">
                {order.is_cancellable ? (
                  <>
                    <button className="apwo-btn-secondary" onClick={() => setCancelOpen(true)} data-testid="order-cancel-btn">
                      Cancel order
                    </button>
                    <button className="apwo-btn-secondary" onClick={onEdit} data-testid="order-edit-btn">
                      Edit order
                    </button>
                  </>
                ) : (
                  <p className="apwo-fineprint" data-testid="order-no-changes-msg">
                    This order can no longer be changed online. Please contact AUTOPARTSB2B.
                  </p>
                )}
              </div>
            )}

            {cancelOpen && (
              <div className="apwo-cancel-modal" data-testid="cancel-modal">
                <p><strong>Cancel this order?</strong></p>
                <textarea
                  placeholder="Reason (optional)"
                  value={cancelReason}
                  maxLength={400}
                  onChange={(e) => setCancelReason(e.target.value)}
                  data-testid="cancel-reason-input"
                />
                {cancelError && <div className="apwo-alert-error" data-testid="cancel-error">{cancelError}</div>}
                <div className="apwo-cancel-modal-actions">
                  <button className="apwo-btn-secondary" onClick={() => setCancelOpen(false)} data-testid="cancel-dismiss-btn">
                    Keep order
                  </button>
                  <button
                    className="apwo-btn-primary apwo-btn-primary--danger"
                    onClick={onCancel}
                    disabled={cancelSubmitting}
                    data-testid="cancel-confirm-btn"
                  >
                    {cancelSubmitting ? "Cancelling…" : "Cancel order"}
                  </button>
                </div>
              </div>
            )}

            <div className="apwo-empty-actions">
              <button className="apwo-btn-secondary" onClick={() => { clear(); nav("/portal/core"); }} data-testid="success-continue">
                Continue browsing
              </button>
              <button className="apwo-btn-secondary" onClick={() => nav("/portal/orders")} data-testid="success-my-orders">
                My orders
              </button>
            </div>
          </aside>
        </div>
      </div>
    </PortalLayout>
  );
}
